/**
 * Blocos de conteudo textual: Bio, Propostas, Conquistas, CustomText,
 * NumberCard, Spacer, Divider.
 */

/* eslint-disable react-refresh/only-export-components */

import { Award, FileText, Hash, Minus, Target, Type, Users } from 'lucide-react';
import type { BlockDefinition, BlockRenderProps } from './types';
import { BlockShell } from './BlockShell';

// =============================================================================
// BioBlock
// =============================================================================

interface BioProps {
  title?: string;
  body?: string;
}

function BioRender({ block, ctx }: BlockRenderProps) {
  const props = block.props as BioProps;
  const body = props.body || ctx.tenant.publicPage.content.biography || '';
  if (!body.trim()) return null;

  const paragraphs = body.split('\n').filter(Boolean);
  const theme = ctx.tenant.theme;

  return (
    <BlockShell block={block} ctx={ctx}>
      <div className="max-w-3xl">
        <h2
          className="font-display text-3xl md:text-4xl font-extrabold mb-6"
          style={{ color: theme.primaryColor }}
        >
          {props.title || 'Sobre'}
        </h2>
        <div className="space-y-4 text-lg text-ink-700 leading-relaxed">
          {paragraphs.map((p, i) => (
            <p key={i}>{p}</p>
          ))}
        </div>
      </div>
    </BlockShell>
  );
}

export const bioBlockDefinition: BlockDefinition<BioProps> = {
  type: 'bio',
  label: 'Biografia',
  description: 'Texto sobre o candidato',
  category: 'content',
  multiple: false,
  icon: Users,
  defaultProps: { title: 'Sobre' },
  Render: BioRender,
};

// =============================================================================
// ProposalsBlock
// =============================================================================

interface ProposalsProps {
  title?: string;
  items?: string[];
  columns?: 1 | 2 | 3;
}

function ProposalsRender({ block, ctx }: BlockRenderProps) {
  const props = block.props as ProposalsProps;
  const items = props.items || ctx.tenant.publicPage.content.proposals || [];
  if (items.length === 0) return null;

  const theme = ctx.tenant.theme;
  const cols = props.columns || 2;
  const gridClass =
    cols === 1
      ? 'grid-cols-1'
      : cols === 3
        ? 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3'
        : 'grid-cols-1 md:grid-cols-2';

  return (
    <BlockShell block={block} ctx={ctx}>
      <div>
        <h2
          className="font-display text-3xl md:text-4xl font-extrabold mb-8"
          style={{ color: theme.primaryColor }}
        >
          {props.title || 'Propostas'}
        </h2>
        <ul className={`grid ${gridClass} gap-4`}>
          {items.map((item, i) => (
            <li
              key={i}
              className="flex items-start gap-4 p-5 rounded-2xl bg-white border border-ink-100 shadow-sm hover:shadow-md transition-shadow"
            >
              <div
                className="flex-shrink-0 h-10 w-10 rounded-xl flex items-center justify-center text-white font-display font-bold"
                style={{ backgroundColor: theme.primaryColor }}
              >
                {i + 1}
              </div>
              <p className="text-ink-700 leading-relaxed pt-1.5">{item}</p>
            </li>
          ))}
        </ul>
      </div>
    </BlockShell>
  );
}

export const proposalsBlockDefinition: BlockDefinition<ProposalsProps> = {
  type: 'proposals',
  label: 'Propostas',
  description: 'Lista de propostas em grid',
  category: 'content',
  multiple: false,
  icon: Target,
  defaultProps: { title: 'Propostas', columns: 2 },
  Render: ProposalsRender,
};

// =============================================================================
// AchievementsBlock
// =============================================================================

interface AchievementsProps {
  title?: string;
  items?: string[];
}

function AchievementsRender({ block, ctx }: BlockRenderProps) {
  const props = block.props as AchievementsProps;
  const items = props.items || ctx.tenant.publicPage.content.achievements || [];
  if (items.length === 0) return null;

  const theme = ctx.tenant.theme;

  return (
    <BlockShell block={block} ctx={ctx}>
      <div>
        <h2
          className="font-display text-3xl md:text-4xl font-extrabold mb-8"
          style={{ color: theme.primaryColor }}
        >
          {props.title || 'Conquistas'}
        </h2>
        <div className="grid md:grid-cols-2 gap-4">
          {items.map((item, i) => (
            <div
              key={i}
              className="bg-white rounded-2xl p-5 border-l-4 shadow-sm hover:shadow-md transition-shadow"
              style={{ borderColor: theme.primaryColor }}
            >
              <p className="text-ink-700 leading-relaxed">{item}</p>
            </div>
          ))}
        </div>
      </div>
    </BlockShell>
  );
}

export const achievementsBlockDefinition: BlockDefinition<AchievementsProps> = {
  type: 'achievements',
  label: 'Conquistas',
  description: 'Realizações importantes',
  category: 'content',
  multiple: false,
  icon: Award,
  defaultProps: { title: 'Conquistas' },
  Render: AchievementsRender,
};

// =============================================================================
// CustomTextBlock
// =============================================================================

interface CustomTextProps {
  title?: string;
  body?: string;
}

function CustomTextRender({ block, ctx }: BlockRenderProps) {
  const props = block.props as CustomTextProps;
  if (!props.body && !props.title) return null;

  const theme = ctx.tenant.theme;
  const paragraphs = (props.body || '').split('\n').filter(Boolean);

  return (
    <BlockShell block={block} ctx={ctx}>
      <div className="max-w-3xl mx-auto">
        {props.title && (
          <h2
            className="font-display text-3xl md:text-4xl font-extrabold mb-6"
            style={{ color: theme.primaryColor }}
          >
            {props.title}
          </h2>
        )}
        <div className="space-y-4 text-lg text-ink-700 leading-relaxed">
          {paragraphs.map((p, i) => (
            <p key={i}>{p}</p>
          ))}
        </div>
      </div>
    </BlockShell>
  );
}

export const customTextBlockDefinition: BlockDefinition<CustomTextProps> = {
  type: 'custom-text',
  label: 'Texto livre',
  description: 'Bloco de texto customizavel',
  category: 'content',
  multiple: true,
  icon: Type,
  defaultProps: { title: '', body: '' },
  Render: CustomTextRender,
};

// =============================================================================
// NumberCardBlock — destaque do numero eleitoral
// =============================================================================

interface NumberCardProps {
  label?: string;
  number?: string;
}

function NumberCardRender({ block, ctx }: BlockRenderProps) {
  const props = block.props as NumberCardProps;
  const num = props.number || ctx.tenant.publicPage.content.candidateNumber;
  if (!num) return null;

  const theme = ctx.tenant.theme;

  return (
    <BlockShell block={block} ctx={ctx}>
      <div className="flex flex-col items-center text-center">
        {props.label && (
          <p className="text-sm md:text-base font-semibold uppercase tracking-widest text-ink-500 mb-3">
            {props.label}
          </p>
        )}
        <div
          className="px-12 py-8 rounded-3xl text-white shadow-2xl"
          style={{ backgroundColor: theme.primaryColor }}
        >
          <div className="flex items-center gap-4">
            <Hash className="h-10 w-10 md:h-14 md:w-14 opacity-50" strokeWidth={3} />
            <span className="font-display text-6xl md:text-8xl font-extrabold tracking-wider">
              {num}
            </span>
          </div>
        </div>
      </div>
    </BlockShell>
  );
}

export const numberCardBlockDefinition: BlockDefinition<NumberCardProps> = {
  type: 'number-card',
  label: 'Número em destaque',
  description: 'Numero eleitoral grande',
  category: 'hero',
  multiple: false,
  icon: Hash,
  defaultProps: { label: 'Vote' },
  Render: NumberCardRender,
};

// =============================================================================
// SpacerBlock — espaço vertical
// =============================================================================

interface SpacerProps {
  size?: 'sm' | 'md' | 'lg' | 'xl';
}

function SpacerRender({ block }: BlockRenderProps) {
  const props = block.props as SpacerProps;
  const sizeMap = { sm: 'h-8', md: 'h-16', lg: 'h-24', xl: 'h-32' };
  const h = sizeMap[props.size || 'md'];
  return <div data-block-id={block.id} data-block-type={block.type} className={h} />;
}

export const spacerBlockDefinition: BlockDefinition<SpacerProps> = {
  type: 'spacer',
  label: 'Espaço',
  description: 'Espaco vertical entre seçoes',
  category: 'layout',
  multiple: true,
  icon: Minus,
  defaultProps: { size: 'md' },
  Render: SpacerRender,
};

// =============================================================================
// DividerBlock — linha divisória
// =============================================================================

function DividerRender({ block, ctx }: BlockRenderProps) {
  const theme = ctx.tenant.theme;
  return (
    <div data-block-id={block.id} data-block-type={block.type} className="container mx-auto px-4 max-w-5xl py-4">
      <hr style={{ borderColor: `${theme.primaryColor}30` }} />
    </div>
  );
}

export const dividerBlockDefinition: BlockDefinition = {
  type: 'divider',
  label: 'Divisor',
  description: 'Linha de separacao',
  category: 'layout',
  multiple: true,
  icon: Minus,
  defaultProps: {},
  Render: DividerRender,
};

// Re-export icons used elsewhere
export { FileText };
